import { LucideIconName } from 'components/LucideIcon'
import isNil from 'lodash/isNil'
import { useMemo } from 'react'
import CantileverTheme from 'styles/cantileverTheme'
import { GeckoEntry, RolodexHistogramFilter } from 'types'

import { useOntology } from './OntologyProvider'
import { OntologyFieldType, OntologyTypeDefinition } from './ontologyTypes'
import { convertKeyToLabel } from './ontologyUtils'
import { useFieldValues } from './useOntologyHistograms'

const DEFAULT_TYPE_COLOR = CantileverTheme.colors.blue['500_base']
const DEFAULT_ICON_NAME: LucideIconName = 'Box'

/**
 * Hook to get the full type definition for an entry type
 * @param entryType The entry type to get the definition for
 * @returns The full OntologyTypeDefinition
 */
export function useTypeDefinition(
  entryType: string
): OntologyTypeDefinition | undefined {
  const { ontology } = useOntology()

  return useMemo(() => ontology.types[entryType], [entryType, ontology.types])
}

/**
 * Hook to get the formatted type name for an entry type
 * @param entryType The entry type to get the display name for
 * @returns The formatted type name
 */
export function useTypeDisplayName(entryType: string): string {
  const typeDef = useTypeDefinition(entryType)

  return useMemo(() => {
    return (
      typeDef?.displayName ||
      entryType.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    )
  }, [typeDef, entryType])
}

/**
 * Hook to get the formatted plural type name for an entry type
 * @param entryType The entry type to get the plural display name for
 * @returns The formatted plural type name
 */
export function usePluralTypeName(entryType: string): string {
  const typeDef = useTypeDefinition(entryType)

  return useMemo(() => {
    return (
      typeDef?.pluralName ||
      entryType.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) +
        's'
    )
  }, [typeDef, entryType])
}

/**
 * Hook to get the type metadata (icon, color) for an entry type
 * @param entryType The entry type to get metadata for
 * @returns Object containing iconName and typeColor
 */
export function useTypeMetadata(entryType: string): {
  iconName: LucideIconName
  typeColor: string
} {
  const typeDef = useTypeDefinition(entryType)

  return useMemo(
    () => ({
      iconName: typeDef?.icon || DEFAULT_ICON_NAME,
      typeColor: typeDef?.color || DEFAULT_TYPE_COLOR,
    }),
    [typeDef]
  )
}

/**
 * Hook to get the display title for an entry using the ontology's title properties
 * @param entry The GeckoEntry to get the display name for
 * @returns The display name for the entry
 */
export function useTitle(entry: GeckoEntry | null | undefined): string {
  const typeDef = useTypeDefinition(entry?.type || '')

  return useMemo(() => {
    if (!entry) return ''

    const titlePropertyName = typeDef?.titleProperty || 'name'
    const baseTitle =
      entry.data?.[titlePropertyName] ??
      (entry.data?.name as string) ??
      entry.id
    return String(baseTitle)
  }, [entry, typeDef])
}

/**
 * Hook to get all fields for a specific type
 * @param entryType The entry type to get fields for
 * @returns Record of field definitions for the type
 */
export function useTypeFields(entryType: string) {
  const typeDef = useTypeDefinition(entryType)

  return useMemo(() => typeDef?.fields || {}, [typeDef])
}

/**
 * Hook to get formatted label from a key
 * @param key The key to convert to a label
 * @returns The formatted label
 */
export function useKeyToLabel(key: string): string {
  return useMemo(() => convertKeyToLabel(key), [key])
}

export const usePreexistingEnumSelectOptions = (
  type: OntologyFieldType,
  fieldKey?: string,
  filters?: readonly RolodexHistogramFilter[]
) => {
  const { values: enumValues, isLoading: isLoadingEnumValues } = useFieldValues(
    fieldKey || '',
    { filters: filters ?? [] }
  )

  const enumOptions = useMemo(() => {
    if (type !== 'enum' || !fieldKey) return []

    return enumValues
      .filter((val) => !isNil(val))
      .map((val) => ({
        value: val,
        label: val,
      }))
  }, [type, fieldKey, enumValues])

  return {
    enumOptions,
    isLoadingEnumValues,
  }
}
